<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisFilterBar from 'dashboard/components-next/LaisFilterBar.vue';
import LaisPropertySearch from 'dashboard/components-next/LaisPropertySearch.vue';
import RelatorioAPI from 'dashboard/api/relatorio';
import ImoveisAPI from 'dashboard/api/imoveis';
import ReportsAPI from 'dashboard/api/reports';
import OnlineDutyAPI from 'dashboard/api/onlineDuty';
import CaptainTasksAPI from 'dashboard/api/captain/tasks';
import ConsentTerm from '../components/ConsentTerm.vue';
import MonthlyReportPanel from '../components/MonthlyReportPanel.vue';
import ActivityMetricsMenu from '../components/ActivityMetricsMenu.vue';
import DutyQueueCarousel from '../components/DutyQueueCarousel.vue';
import PerformanceDisplay from '../components/PerformanceDisplay.vue';
import ReportPropertyAnalysisCard from '../components/ReportPropertyAnalysisCard.vue';
import OwnerShareCard from '../components/OwnerShareCard.vue';
import PriceChangeCard from '../components/PriceChangeCard.vue';
import ReportPerformanceCard from '../components/ReportPerformanceCard.vue';
import ReportPerformanceInsights from '../components/ReportPerformanceInsights.vue';
import ReportOverviewSection from '../components/ReportOverviewSection.vue';
import ReportRankingTabs from '../components/ReportRankingTabs.vue';
import ReportLoadingSkeleton from '../components/ReportLoadingSkeleton.vue';
import ReportErrorPanel from '../components/ReportErrorPanel.vue';
import ReportActivityCards from '../components/ReportActivityCards.vue';
import ReportRankingCard from '../components/ReportRankingCard.vue';
import ReportRankingSidebar from '../components/ReportRankingSidebar.vue';
import ReportAnalyticsSection from '../components/ReportAnalyticsSection.vue';
import ReportAnalyticsTimelineCard from '../components/ReportAnalyticsTimelineCard.vue';
import DutyQueueToolbar from '../components/DutyQueueToolbar.vue';
import DutyPendingList from '../components/DutyPendingList.vue';
import DutyInProgressList from '../components/DutyInProgressList.vue';
import SupportTemplateLibrary from '../components/SupportTemplateLibrary.vue';
import DesempenhoEquipe from './DesempenhoEquipe.vue';
import MetasIndex from './MetasIndex.vue';
import { useStore } from 'dashboard/composables/store';
import { hasPermissions } from 'dashboard/helper/permissionsHelper';
import { useAlert, useTracking } from 'dashboard/composables';
import { trackQuintoEvent } from 'dashboard/helper/lais/trackingEvents';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  currentMonthKey,
  formatBRL,
  formatCompactNumber,
  formatPercent,
  formatWaitingTime,
  isIntegerCurrencyValue,
  monthlyPeriodOptions,
  parseCurrencyValue,
  periodRange,
  propertyOptionLabel,
} from '../utils/reportFormatters';
import {
  ANALYTICS_SOURCE_KEYS,
  REPORT_SECTION_KEYS,
  REPORT_TAB_KEYS,
  WORKSPACE_TAB_KEYS,
  analyticsSourceOptions,
  baseReportSections,
  exportFormats,
  workspaceTabs,
} from '../utils/reportConstants';
import {
  normalizedExternalImovelPayload,
  valueFrom,
} from '../utils/externalImovel';
import { resolveSupportBody } from '../utils/dutySupportTemplates';
import {
  compactQuery,
  querySignature,
  sumMetricValueByPatterns,
} from '../utils/analyticsMetrics';
import { useAgentConsents } from '../composables/useAgentConsents';
import { useReportExport } from '../composables/useReportExport';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { track } = useTracking();
const currentPermissions = computed(
  () => store.getters.getCurrentAccount?.permissions || []
);
const canSeeWorkspaceTab = item =>
  !item.permissions ||
  !item.permissions.length ||
  hasPermissions(item.permissions, currentPermissions.value);
const routeString = value => (Array.isArray(value) ? value[0] : value);
const workspaceFromRoutePath = () => {
  const path = route.path || '';
  if (
    path.includes('/produtividade') ||
    path.includes('/minha-atividade') ||
    path.includes('/atividade')
  ) {
    return 'activity';
  }
  if (
    path.includes('/plantao') ||
    path.includes('/atendimento-online') ||
    path.includes('/fila')
  ) {
    return 'duty';
  }
  if (path.includes('desempenho-equipe') || path.includes('team-performance')) {
    return 'team';
  }
  if (
    path.includes('/metas') ||
    path.includes('targets') ||
    path.includes('team-targets')
  ) {
    return 'targets';
  }
  return 'report';
};
const routeWorkspaceTabValue = () =>
  WORKSPACE_TAB_KEYS.includes(routeString(route.query?.workspace))
    ? routeString(route.query.workspace)
    : workspaceFromRoutePath();
const routeOperacaoValue = () =>
  ['aluguel', 'venda'].includes(routeString(route.query?.operacao))
    ? routeString(route.query.operacao)
    : 'aluguel';
const routePeriodoValue = () =>
  /^\d{4}-\d{2}$/.test(
    routeString(route.query?.periodo || route.query?.period) || ''
  )
    ? routeString(route.query.periodo || route.query.period)
    : currentMonthKey();
const routePropertyIdValue = () =>
  route.query?.imovel_id ? String(routeString(route.query.imovel_id)) : '';
const routeReportSectionValue = operacaoValue => {
  const routeReportSection = REPORT_SECTION_KEYS.includes(
    routeString(route.query?.report_section || route.query?.section)
  )
    ? routeString(route.query.report_section || route.query.section)
    : 'published';

  return operacaoValue === 'venda' || routeReportSection !== 'ccv'
    ? routeReportSection
    : 'published';
};
const routeReportTabValue = () =>
  REPORT_TAB_KEYS.includes(routeString(route.query?.tab))
    ? routeString(route.query.tab)
    : 'imoveis';
const routeAnalyticsSourceValue = () =>
  ANALYTICS_SOURCE_KEYS.includes(
    routeString(route.query?.origem || route.query?.source)
  )
    ? routeString(route.query.origem || route.query.source)
    : 'all';
const initialOperacao = routeOperacaoValue();
const initialPeriodo = routePeriodoValue();
const initialPropertyId = routePropertyIdValue();
const initialReportSection = routeReportSectionValue(initialOperacao);
const initialTab = routeReportTabValue();
const initialAnalyticsSource = routeAnalyticsSourceValue();
const initialWorkspaceTab = routeWorkspaceTabValue();

const workspaceTab = ref(initialWorkspaceTab);
const operacao = ref(initialOperacao);
const periodo = ref(initialPeriodo);
const tab = ref(initialTab);
const reportSection = ref(initialReportSection);
const selectedPropertyId = ref(initialPropertyId);
const selectedReportProperty = ref(null);
const selectedPropertySource = ref('portfolio');
const reportPropertyQuery = ref(routeString(route.query?.q) || '');
const propertyMaterializing = ref(false);
const reportLoading = ref(false);
const reportError = ref('');
const agentProductivity = ref(null);
const agentProductivityLoading = ref(false);
const agentProductivityError = ref('');
const onlineDutyPending = ref([]);
const onlineDutyInProgress = ref([]);
const onlineDutyLoading = ref(false);
const onlineDutyError = ref('');
const onlineDutySort = ref('older-tta');
const claimingDutyConversationId = ref(null);
const supportStatusSavingId = ref(null);
const supportStatusDrafts = ref({});
const captainSuggestionLoadingId = ref(null);
const captainSuggestions = ref({});

const trackDutyEvent = (event, properties = {}) => {
  try {
    trackQuintoEvent(track, event, {
      domain: 'online_support',
      sort: onlineDutySort.value,
      ...properties,
    });
  } catch {
    // Tracking must not block online duty flows.
  }
};

const {
  agentConsentsLoading,
  agentConsentSaving,
  ambassadorConsent,
  loadAgentConsents,
  acceptAmbassadorConsent,
  revokeAmbassadorConsent,
} = useAgentConsents();

const resolveAllowedWorkspaceTab = key => {
  const target = workspaceTabs.find(item => item.value === key);
  return target && !canSeeWorkspaceTab(target) ? 'report' : key;
};

const dadosImoveis = ref([]);
const dadosBairros = ref([]);
const dadosTipos = ref([]);
const dadosImoveisNoBairro = ref([]);
const bairroDestaque = ref('');
const precoMediano = ref({
  valor: null,
  total: 0,
  operacao: 'todos',
});
const analyticsSource = ref(initialAnalyticsSource);
const analyticsFunnel = ref([]);
const analyticsTopEvents = ref([]);
const analyticsSources = ref([]);
const analyticsTimeline = ref([]);
const analyticsTotal = ref(0);
const lastUpdatedAt = ref(null);
const priceEditValue = ref('');
const isSavingPrice = ref(false);
const performanceSetup = ref({
  property: null,
  price_analysis: {
    current_value: null,
    suggested_value: null,
    min_suggested_value: null,
    max_suggested_value: null,
    price_per_area: null,
    recommendation_percentage: 0,
    status: 'unavailable',
    samples: 0,
    business_context: 'rental',
  },
  portfolio: {
    offers_sent: 0,
    offers_accepted: 0,
    offer_conversion: 0,
    offer_conversion_delta: 0,
    visits_scheduled: 0,
    visits_realized: 0,
    visit_conversion: 0,
    published_listings: 0,
    traffic: 0,
    traffic_average_per_week: 0,
  },
  owner_share: {
    url: '',
    whatsapp_text: '',
    cover_title: 'Análise de preço do seu imóvel',
    cover_text:
      'Em parceria com a sua imobiliária, preparamos uma análise completa para te ajudar com o preço ideal do seu imóvel',
    summary_title: 'Olá, proprietário.',
    summary_message:
      'Nós da Imudei, realizamos uma análise do seu imóvel e encontramos o preço ideal.',
    property_alert_title: 'Seu imóvel:',
    property_alert_message: '',
  },
  copy: {},
});
let isApplyingReportRouteState = false;
let isSyncingReportRouteState = false;
let realtimeRefreshTimer = null;
const periodos = computed(() => monthlyPeriodOptions());

const visibleWorkspaceTabs = computed(() =>
  workspaceTabs.filter(canSeeWorkspaceTab)
);
const activeWorkspaceTab = computed(
  () =>
    workspaceTabs.find(item => item.value === workspaceTab.value) ||
    workspaceTabs[0]
);
const isReportWorkspace = computed(() => workspaceTab.value === 'report');
const workspaceHeaderTitle = computed(() => {
  if (workspaceTab.value === 'activity') return 'Minha Atividade';
  if (workspaceTab.value === 'duty') return 'Plantão';
  if (workspaceTab.value === 'consent') return 'Embaixador';

  return performanceSetup.value.copy?.title_header || 'Relatorio de Desempenho';
});
const workspaceHeaderHelper = computed(() => {
  if (workspaceTab.value === 'activity') {
    return 'Meta, progresso e indicadores pessoais do corretor no período selecionado.';
  }

  if (workspaceTab.value === 'duty') {
    return 'Fila online para iniciar atendimentos e acompanhar clientes em andamento.';
  }

  if (workspaceTab.value === 'consent') {
    return 'Aceite e revogação do programa de embaixadores por propósito registrado.';
  }

  return (
    performanceSetup.value.copy?.subtitle_header ||
    'A Imudei preparou uma análise de resultado dos últimos 30 dias do seu imóvel.'
  );
});

const reportSections = computed(() =>
  baseReportSections.filter(
    section => operacao.value === 'venda' || section.value !== 'ccv'
  )
);

const items = computed(() =>
  tab.value === 'imoveis' ? dadosImoveis.value : dadosBairros.value
);
const topItems = computed(() => items.value.slice(0, 10));
const total = computed(() => items.value.reduce((s, i) => s + i.valor, 0));
const totalTipos = computed(() =>
  dadosTipos.value.reduce((sum, item) => sum + item.valor, 0)
);
const totalBairroDestaque = computed(() =>
  dadosImoveisNoBairro.value.reduce((sum, item) => sum + item.valor, 0)
);
const analyticsTimelineTotal = computed(() =>
  analyticsTimeline.value.reduce((sum, item) => sum + item.valor, 0)
);
const mainTitle = computed(() =>
  tab.value === 'imoveis' ? 'Top 10 imóveis' : 'Bairros mais procurados'
);
const mainDescription = computed(
  () => `${total.value} demonstrações de interesse no período`
);
const topTipoLabel = computed(() => dadosTipos.value[0]?.label || '');
const topTipoValor = computed(() => Number(dadosTipos.value[0]?.valor || 0));
const reportNarratives = computed(() => {
  const narratives = [];

  if (topTipoLabel.value) {
    narratives.push(
      `Entre seus leads, imóveis com [b]${topTipoLabel.value}[/b] concentraram ${topTipoValor.value} demonstrações de interesse.`
    );
    narratives.push(
      `Imóveis do tipo [b]${topTipoLabel.value}[/b] são os que fizeram mais sucesso na região.`
    );
  }

  if (bairroDestaque.value) {
    narratives.push(
      `Saiba quais imóveis de [b]${bairroDestaque.value}[/b] receberam mais interesse.`
    );
    narratives.push(
      `Seus imóveis em [b]${bairroDestaque.value}[/b] aparecem como destaque na prateleira de imóveis.`
    );
    narratives.push(
      `Compare [b]${bairroDestaque.value}[/b] com sua prateleira de imóveis.`
    );
    narratives.push(
      `Os imóveis em [b]${bairroDestaque.value}[/b] da sua prateleira atraíram mais leads.`
    );
  }

  return narratives.slice(0, 6);
});
const analyticsSourceParams = computed(() => ({
  source: analyticsSource.value === 'all' ? undefined : analyticsSource.value,
}));
const { exportingFormat, exportarRelatorio } = useReportExport({
  periodo,
  operacao,
  selectedPropertyId,
  analyticsSource,
  analyticsSourceParams,
});
const lastUpdatedLabel = computed(() =>
  lastUpdatedAt.value
    ? lastUpdatedAt.value.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'
);
const strongestFunnelValue = computed(
  () => Math.max(...analyticsFunnel.value.map(item => item.valor), 0) || 1
);
const hasExplicitReportProperty = computed(() =>
  Boolean(selectedPropertyId.value)
);
const reportMetricSubject = computed(() =>
  hasExplicitReportProperty.value ? 'este imóvel' : 'os imóveis da sua carteira'
);
const reportMetricAccessTarget = computed(() =>
  hasExplicitReportProperty.value
    ? 'neste imóvel'
    : 'nos imóveis da sua carteira'
);
const reportMetricPerformanceTarget = computed(() =>
  hasExplicitReportProperty.value
    ? 'deste imóvel'
    : 'dos seus imóveis disponíveis'
);
const reportMetricVerb = computed(() =>
  hasExplicitReportProperty.value ? 'teve' : 'tiveram'
);
const reportMetricScopeBadge = computed(() =>
  hasExplicitReportProperty.value ? 'Recorte: este imóvel' : 'Recorte: carteira'
);
const proposalOperationLabel = computed(() =>
  operacao.value === 'aluguel' ? 'aluguel' : 'venda'
);

const metricValueByPatterns = patterns =>
  sumMetricValueByPatterns(
    [...analyticsFunnel.value, ...analyticsTopEvents.value],
    patterns
  );

const viewsCount = computed(() =>
  metricValueByPatterns(['visualizacao', 'visualizacoes', 'view'])
);
const scheduledVisitsCount = computed(() =>
  metricValueByPatterns(['visita agendada', 'visitas agendadas', 'visit'])
);
const adActivitiesCount = computed(() => analyticsTotal.value || total.value);
const performanceCopy = computed(() => performanceSetup.value.copy || {});
const performanceScore = computed(() => {
  if (!adActivitiesCount.value) return 0;

  const score =
    Number(adActivitiesCount.value || 0) * 0.8 +
    Number(viewsCount.value || 0) * 0.2 +
    Number(scheduledVisitsCount.value || 0) * 8 +
    analyticsSources.value.length * 4;

  return Math.min(100, Math.max(1, Math.round(score)));
});

const selectedProperty = computed(() => performanceSetup.value.property || {});
const selectedPropertyOwner = computed(
  () => selectedProperty.value.owner || {}
);
const priceAnalysis = computed(
  () => performanceSetup.value.price_analysis || {}
);
const portfolio = computed(() => performanceSetup.value.portfolio || {});
const activeReportSection = computed(
  () =>
    reportSections.value.find(
      section => section.value === reportSection.value
    ) || reportSections.value[0]
);
const activeReportSectionDescription = computed(() => {
  if (
    activeReportSection.value?.value === 'published' &&
    hasExplicitReportProperty.value
  ) {
    return 'Anúncio publicado na Imudei até o momento e o tráfego, considerando o período selecionado.';
  }

  return activeReportSection.value?.description || '';
});

const ownerShare = computed(() => performanceSetup.value.owner_share || {});
const isRentalReport = computed(
  () =>
    selectedProperty.value.finalidade === 'aluguel' ||
    operacao.value === 'aluguel'
);
const priceField = computed(() =>
  operacao.value === 'aluguel' ? 'valor_aluguel' : 'valor_venda'
);

const findMaterializedImovel = async payload => {
  if (!payload.external_id) return null;
  const { data } = await ImoveisAPI.get({
    q: payload.external_id,
    page: 1,
    per_page: 10,
  });
  return (data.payload || []).find(
    item => String(item.external_id || '') === String(payload.external_id)
  );
};

const materializeExternalImovel = async (item, source) => {
  const payload = normalizedExternalImovelPayload(item, source, operacao.value);
  const existing = await findMaterializedImovel(payload);
  if (existing) return existing;

  const { data } = await ImoveisAPI.create(payload);
  return data.payload || data;
};

const selectReportProperty = async result => {
  selectedPropertySource.value = result.source;
  selectedReportProperty.value = result.item;

  if (result.source === 'portfolio') {
    selectedPropertyId.value = String(valueFrom(result.item, 'id'));
    return;
  }

  propertyMaterializing.value = true;
  try {
    const internalImovel = await materializeExternalImovel(
      result.item,
      result.source
    );
    selectedReportProperty.value = internalImovel;
    selectedPropertySource.value = 'portfolio';
    selectedPropertyId.value = String(valueFrom(internalImovel, 'id'));
    useAlert('Imóvel externo vinculado à carteira para gerar o relatório.');
  } catch {
    selectedPropertyId.value = '';
    useAlert('Não foi possível vincular este imóvel externo ao relatório.');
  } finally {
    propertyMaterializing.value = false;
  }
};

const activityPeriodRange = computed(() => periodRange(periodo.value));
const activityPeriodLabel = computed(
  () => `${activityPeriodRange.value.inicio} a ${activityPeriodRange.value.fim}`
);
const productivityTarget = computed(
  () => agentProductivity.value?.target || {}
);
const productivityProgress = computed(() =>
  Math.min(100, Math.max(0, Number(productivityTarget.value.progress || 0)))
);
const productivityActual = computed(() =>
  Number(productivityTarget.value.actual || 0)
);
const productivityTargetValue = computed(() =>
  Number(productivityTarget.value.target_value || 0)
);
const productivityGroups = computed(() => {
  const clients = agentProductivity.value?.clients || {};
  const visits = agentProductivity.value?.visits || {};
  const offers = agentProductivity.value?.offers || {};

  return [
    {
      key: 'clients',
      title: 'Clientes',
      icon: 'i-lucide-users',
      metrics: [
        {
          label: 'Clientes com atividade',
          value: clients.client_with_activity ?? clients.clientWithActivity,
        },
        {
          label: 'Clientes fixados',
          value: clients.preferred_fixed_client ?? clients.preferredFixedClient,
        },
      ],
    },
    {
      key: 'visits',
      title: 'Visitas',
      icon: 'i-lucide-calendar-check',
      metrics: [
        {
          label: 'Visitas finalizadas',
          value: visits.finished_visits ?? visits.finishedVisits,
        },
        {
          label: 'Visitas agendadas',
          value: visits.total_booked_visits ?? visits.totalBookedVisits,
        },
        {
          label: 'Agendadas pelo corretor',
          value: visits.visits_booked_by_agent ?? visits.visitsBookedByAgent,
        },
      ],
    },
    {
      key: 'offers',
      title: 'Propostas',
      icon: 'i-lucide-file-check-2',
      metrics: [
        {
          label: 'Propostas enviadas',
          value: offers.sent_offers ?? offers.sentOffers,
        },
        {
          label: 'Propostas aceitas',
          value: offers.accepted_offers ?? offers.acceptedOffers,
        },
        {
          label: 'CCVs assinados',
          value: offers.signed_ccv ?? offers.signedCCV,
        },
      ],
    },
  ];
});

const hasOnlineDutyItems = computed(
  () => onlineDutyPending.value.length || onlineDutyInProgress.value.length
);
const supportCommentFor = conversation =>
  supportStatusDrafts.value[conversation.id] ??
  conversation?.duty?.agent_comment ??
  '';
const setSupportComment = (conversation, value) => {
  supportStatusDrafts.value = {
    ...supportStatusDrafts.value,
    [conversation.id]: value,
  };
};
const purchaseCommitmentsCount = computed(
  () =>
    Number(
      portfolio.value.purchase_commitments ||
        portfolio.value.compromissos_compra_venda ||
        portfolio.value.ccvs ||
        portfolio.value.ccv_count ||
        0
    ) || 0
);

const reportSectionCards = computed(() => {
  if (reportSection.value === 'visits') {
    return [
      {
        title: 'Visitas agendadas',
        value: portfolio.value.visits_scheduled || 0,
        description: `Visitas agendadas para ${reportMetricSubject.value} durante o período selecionado.`,
      },
      {
        title: 'Visitas realizadas',
        value: portfolio.value.visits_realized || 0,
        description: `Visitas agendadas e que foram executadas para ${reportMetricSubject.value} durante o período selecionado.`,
      },
      {
        title: 'Conversão de visitas',
        value: formatPercent(portfolio.value.visit_conversion),
        description:
          'Evolução e taxa de conversão de visitas, considerando visitas agendadas e realizadas dentro do período selecionado.',
        highlight: true,
      },
    ];
  }

  if (reportSection.value === 'offers') {
    return [
      {
        title: 'Propostas enviadas',
        value: portfolio.value.offers_sent || 0,
        description: `Propostas de ${proposalOperationLabel.value} enviadas para ${reportMetricSubject.value} durante o período selecionado.`,
      },
      {
        title: 'Propostas aceitas',
        value: portfolio.value.offers_accepted || 0,
        description: `Propostas de ${proposalOperationLabel.value} aceitas para ${reportMetricSubject.value} durante o período selecionado.`,
      },
      {
        title: 'Conversão de ofertas',
        value: formatPercent(portfolio.value.offer_conversion),
        description:
          'Evolução e taxa de conversão de propostas, considerando propostas enviadas e propostas aceitas dentro do período selecionado.',
        highlight: true,
      },
    ];
  }

  if (reportSection.value === 'ccv') {
    return [
      {
        title: 'Compromissos de compra e venda',
        value: purchaseCommitmentsCount.value,
        description: `Compromissos de compra e venda registrados para ${reportMetricSubject.value} durante o período selecionado.`,
      },
      {
        title: 'Propostas aceitas',
        value: portfolio.value.offers_accepted || 0,
        description:
          'Propostas aceitas que podem avançar para compromissos de compra e venda.',
      },
      {
        title: 'Status do indicador',
        value: purchaseCommitmentsCount.value ? 'Ativo' : 'Sem dados',
        description:
          'Os compromissos de compra e venda serão exibidos quando houver dados suficientes no período selecionado.',
        highlight: true,
      },
    ];
  }

  return [
    {
      title: 'Anúncios publicados (até a data)',
      value: portfolio.value.published_listings || 0,
      description: hasExplicitReportProperty.value
        ? 'Anúncio publicado na Imudei até o momento.'
        : 'Total acumulado de imóveis publicados na Imudei até o momento.',
    },
    {
      title: 'Tráfego médio no período',
      value: `${formatCompactNumber(portfolio.value.traffic_average_per_week)}/semana`,
      description: `Média semanal de acessos ${reportMetricAccessTarget.value} durante o período selecionado.`,
    },
    {
      title: 'Tráfego (acessos na Imudei)',
      value: formatCompactNumber(portfolio.value.traffic),
      description:
        'Número de acessos na Imudei, considerando o período selecionado.',
      highlight: true,
    },
  ];
});

const selectedPropertyLabel = computed(() =>
  selectedProperty.value.id ? propertyOptionLabel(selectedProperty.value) : ''
);

const priceRangeValidationMessage = computed(() => {
  if (!priceEditValue.value) return '';
  if (!isIntegerCurrencyValue(priceEditValue.value)) {
    return 'Apenas valores inteiros são permitidos';
  }

  const price = parseCurrencyValue(priceEditValue.value);
  const min = Number(priceAnalysis.value.min_suggested_value || 0);
  const max = Number(priceAnalysis.value.max_suggested_value || 0);
  if (price && min > 0 && max > 0 && (price < min || price > max)) {
    return `O valor deve estar entre ${formatBRL(min)} e ${formatBRL(max)}`;
  }

  return '';
});

const analyticsSummaryText = computed(() => {
  if (!analyticsTotal.value) {
    return 'Ainda não há eventos suficientes para montar o funil deste período.';
  }

  return `${analyticsTotal.value} eventos foram capturados na janela filtrada.`;
});

const loadAnalytics = async params => {
  const [funnelRes, eventsRes] = await Promise.all([
    RelatorioAPI.analyticsFunil({
      ...params,
      ...analyticsSourceParams.value,
    }),
    RelatorioAPI.analyticsEventos({
      ...params,
      ...analyticsSourceParams.value,
    }),
  ]);

  analyticsFunnel.value = funnelRes.data.data || [];
  analyticsTopEvents.value = eventsRes.data.top_events || [];
  analyticsSources.value = eventsRes.data.sources || [];
  analyticsTimeline.value = eventsRes.data.timeline || [];
  analyticsTotal.value = eventsRes.data.total || 0;
};

const load = async () => {
  const { inicio, fim } = periodRange(periodo.value);
  const params = {
    operacao: operacao.value === 'aluguel' ? 'aluguel' : 'venda',
    inicio,
    fim,
  };
  const performanceParams = {
    ...params,
    imovel_id: selectedPropertyId.value || undefined,
  };
  reportLoading.value = true;
  reportError.value = '';
  try {
    const [imoveisRes, bairrosRes, tiposRes, precoRes, performanceRes] =
      await Promise.all([
        RelatorioAPI.imoveisMaisProcurados(params),
        RelatorioAPI.bairrosMaisProcurados(params),
        RelatorioAPI.tiposMaisProcurados(params),
        RelatorioAPI.precoMediano(params),
        RelatorioAPI.performanceSetup(performanceParams),
      ]);
    dadosImoveis.value = imoveisRes.data.data || [];
    dadosBairros.value = bairrosRes.data.data || [];
    dadosTipos.value = tiposRes.data.data || [];
    precoMediano.value = precoRes.data || precoMediano.value;
    performanceSetup.value = performanceRes.data || performanceSetup.value;
    if (performanceSetup.value.property?.id) {
      selectedReportProperty.value = performanceSetup.value.property;
      selectedPropertySource.value = 'portfolio';
      if (!reportPropertyQuery.value) {
        reportPropertyQuery.value = propertyOptionLabel(
          performanceSetup.value.property
        );
      }
    }
    priceEditValue.value = String(
      Math.round(
        performanceSetup.value.price_analysis?.suggested_value ||
          performanceSetup.value.price_analysis?.current_value ||
          0
      )
    );
    bairroDestaque.value = dadosBairros.value[0]?.label || '';

    if (bairroDestaque.value) {
      const { data } = await RelatorioAPI.imoveisMaisProcurados({
        ...params,
        bairro: bairroDestaque.value,
      });
      dadosImoveisNoBairro.value = data.data || [];
    } else {
      dadosImoveisNoBairro.value = [];
    }

    await loadAnalytics(params);
    lastUpdatedAt.value = new Date();
  } catch (e) {
    reportError.value = 'Os resultados não puderam ser carregados.';
    useAlert('Não foi possível carregar os relatórios.');
  } finally {
    reportLoading.value = false;
  }
};

const loadAgentProductivity = async () => {
  const { inicio, fim } = activityPeriodRange.value;
  agentProductivityLoading.value = true;
  agentProductivityError.value = '';
  try {
    const { data } = await ReportsAPI.agentProductivity({
      since: inicio,
      until: fim,
    });
    agentProductivity.value = data;
  } catch {
    agentProductivityError.value = 'Não foi possível carregar sua atividade.';
  } finally {
    agentProductivityLoading.value = false;
  }
};

const loadOnlineDuty = async () => {
  onlineDutyLoading.value = true;
  onlineDutyError.value = '';
  try {
    const [pendingRes, inProgressRes] = await Promise.all([
      OnlineDutyAPI.pending({ limit: 100 }),
      OnlineDutyAPI.inProgress({ sort: onlineDutySort.value, per_page: 10 }),
    ]);
    onlineDutyPending.value = pendingRes.data.payload || [];
    onlineDutyInProgress.value = inProgressRes.data.payload || [];
    trackDutyEvent('tta_page_viewed', {
      pending_count: onlineDutyPending.value.length,
      in_progress_count: onlineDutyInProgress.value.length,
    });
  } catch {
    onlineDutyError.value = 'Não foi possível carregar o plantão.';
  } finally {
    onlineDutyLoading.value = false;
  }
};

const claimOnlineDutyConversation = async conversation => {
  claimingDutyConversationId.value = conversation.id;
  trackDutyEvent('tta_card_opened', {
    conversation_id: conversation.id,
    status: conversation.duty?.status || 'pending',
  });
  try {
    await OnlineDutyAPI.claim(conversation.id);
    useAlert('Atendimento iniciado.');
    trackDutyEvent('tta_status_changed', {
      conversation_id: conversation.id,
      status: 'in_progress',
    });
    await loadOnlineDuty();
  } catch (error) {
    if (error?.response?.status === 409) {
      const reason = error?.response?.data?.error;
      useAlert(
        reason === 'transferred'
          ? 'Este atendimento foi transferido.'
          : 'Este atendimento já foi iniciado por outro corretor.'
      );
      await loadOnlineDuty();
      return;
    }

    useAlert('Não foi possível iniciar o atendimento.');
  } finally {
    claimingDutyConversationId.value = null;
  }
};

const updateSupportStatus = async (conversation, status) => {
  supportStatusSavingId.value = `${conversation.id}:${status}`;
  try {
    await OnlineDutyAPI.updateSupportStatus(conversation.id, {
      status,
      agentComment: supportCommentFor(conversation),
    });
    useAlert(
      status === 'complete'
        ? 'Atendimento concluído.'
        : 'Status do atendimento atualizado.'
    );
    trackDutyEvent('agent_support_dialog_closed', {
      conversation_id: conversation.id,
      status,
    });
    trackDutyEvent('tta_status_changed', {
      conversation_id: conversation.id,
      status,
    });
    setSupportComment(conversation, '');
    await loadOnlineDuty();
  } catch {
    useAlert('Não foi possível atualizar o atendimento.');
  } finally {
    supportStatusSavingId.value = null;
  }
};

// Copia a resposta sugerida resolvida pela origem/tipo da conversa (SPEC 28 §4.2/§7.3).
const copyResolvedSupport = async conversation => {
  try {
    await navigator.clipboard.writeText(resolveSupportBody(conversation));
    useAlert('Resposta copiada.');
  } catch {
    useAlert('Não foi possível copiar a resposta.');
  }
};

const openDutyConversation = conversation => {
  trackDutyEvent('chatcrpp_access_chat_clicked', {
    conversation_id: conversation.id,
  });
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: conversation.id,
    },
  });
};

const requestCaptainSuggestion = async conversation => {
  captainSuggestionLoadingId.value = conversation.id;
  try {
    const { data } = await CaptainTasksAPI.replySuggestion(conversation.id);
    if (!data?.message) {
      useAlert('Captain não retornou sugestão para esta conversa.');
      return;
    }

    captainSuggestions.value = {
      ...captainSuggestions.value,
      [conversation.id]: data.message,
    };
    useAlert('Sugestão Captain gerada.');
  } catch {
    useAlert('Captain não está disponível para esta conversa.');
  } finally {
    captainSuggestionLoadingId.value = null;
  }
};

const copyCaptainSuggestion = async conversation => {
  const suggestion = captainSuggestions.value[conversation.id];
  if (!suggestion) return;

  try {
    await navigator.clipboard.writeText(suggestion);
    useAlert('Sugestão Captain copiada.');
  } catch {
    useAlert('Não foi possível copiar a sugestão Captain.');
  }
};

const loadActiveWorkspace = async () => {
  if (workspaceTab.value === 'activity') {
    await loadAgentProductivity();
  } else if (workspaceTab.value === 'duty') {
    await loadOnlineDuty();
  } else if (workspaceTab.value === 'consent') {
    await loadAgentConsents();
  }
};

const handleOnlineDutyRealtimeUpdate = () => {
  if (workspaceTab.value === 'duty') {
    loadOnlineDuty();
  }
};

const savePriceChange = async () => {
  const propertyId = selectedProperty.value.id;
  const price = parseCurrencyValue(priceEditValue.value);
  if (!propertyId || !price) {
    useAlert('Apenas valores inteiros são permitidos');
    return;
  }

  if (priceRangeValidationMessage.value) {
    useAlert(priceRangeValidationMessage.value);
    return;
  }

  isSavingPrice.value = true;
  try {
    await ImoveisAPI.update(propertyId, {
      [priceField.value]: price,
    });
    useAlert('O preço do imóvel foi alterado com sucesso!');
    await load();
  } catch {
    useAlert('Erro ao salvar preço. Tente salvar dentro de alguns instantes.');
  } finally {
    isSavingPrice.value = false;
  }
};

const syncReportRouteState = () => {
  const defaultWorkspace = workspaceFromRoutePath();
  const nextQuery = compactQuery({
    ...route.query,
    operacao: operacao.value,
    periodo: periodo.value,
    period: undefined,
    origem: analyticsSource.value === 'all' ? undefined : analyticsSource.value,
    source: undefined,
    imovel_id: selectedPropertyId.value || undefined,
    report_section: reportSection.value,
    section: undefined,
    tab: tab.value === 'imoveis' ? undefined : tab.value,
    q: reportPropertyQuery.value || undefined,
    workspace:
      workspaceTab.value === defaultWorkspace ? undefined : workspaceTab.value,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  isSyncingReportRouteState = true;
  router
    .replace({ query: nextQuery })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingReportRouteState = false;
      }, 0);
    });
};

const applyReportRouteState = () => {
  isApplyingReportRouteState = true;
  const nextOperacao = routeOperacaoValue();
  operacao.value = nextOperacao;
  periodo.value = routePeriodoValue();
  analyticsSource.value = routeAnalyticsSourceValue();
  selectedPropertyId.value = routePropertyIdValue();
  selectedReportProperty.value = null;
  selectedPropertySource.value = 'portfolio';
  reportSection.value = routeReportSectionValue(nextOperacao);
  tab.value = routeReportTabValue();
  workspaceTab.value = resolveAllowedWorkspaceTab(routeWorkspaceTabValue());
  reportPropertyQuery.value = routeString(route.query?.q) || '';
  setTimeout(() => {
    isApplyingReportRouteState = false;
  }, 0);
};

watch([periodo, analyticsSource, selectedPropertyId], async () => {
  if (isApplyingReportRouteState) return;
  syncReportRouteState();
  await load();
  if (workspaceTab.value === 'activity') {
    await loadAgentProductivity();
  }
});
watch([reportSection, tab], () => {
  if (isApplyingReportRouteState) return;
  syncReportRouteState();
});
watch(workspaceTab, async () => {
  if (isApplyingReportRouteState) return;
  syncReportRouteState();
  await loadActiveWorkspace();
});
watch(onlineDutySort, async () => {
  if (workspaceTab.value === 'duty') {
    await loadOnlineDuty();
  }
});
watch(operacao, async () => {
  if (isApplyingReportRouteState) return;
  selectedPropertyId.value = '';
  selectedReportProperty.value = null;
  selectedPropertySource.value = 'portfolio';
  if (reportSection.value === 'ccv' && operacao.value !== 'venda') {
    reportSection.value = 'published';
  }
  syncReportRouteState();
  await load();
});
watch(
  () => [
    route.query.operacao,
    route.query.periodo,
    route.query.period,
    route.query.origem,
    route.query.source,
    route.query.imovel_id,
    route.query.report_section,
    route.query.section,
    route.query.tab,
    route.query.workspace,
    route.query.q,
  ],
  async () => {
    if (isSyncingReportRouteState) return;
    applyReportRouteState();
    await load();
    await loadActiveWorkspace();
  }
);
onMounted(async () => {
  emitter.on(BUS_EVENTS.ONLINE_DUTY_UPDATED, handleOnlineDutyRealtimeUpdate);
  workspaceTab.value = resolveAllowedWorkspaceTab(workspaceTab.value);
  await load();
  await loadActiveWorkspace();
  realtimeRefreshTimer = window.setInterval(async () => {
    if (workspaceTab.value === 'report') {
      await load();
    } else {
      await loadActiveWorkspace();
    }
  }, 60000);
});
onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.ONLINE_DUTY_UPDATED, handleOnlineDutyRealtimeUpdate);
  if (realtimeRefreshTimer) window.clearInterval(realtimeRefreshTimer);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="h-full min-w-0 overflow-y-auto overflow-x-hidden bg-white font-redHatText"
  >
    <LaisHeader :title="workspaceHeaderTitle" :helper="workspaceHeaderHelper">
      <template #actions>
        <div
          v-if="isReportWorkspace"
          class="flex flex-wrap items-center justify-end gap-2"
        >
          <span class="text-[12px] font-medium text-lais-text-muted">
            Atualizado às {{ lastUpdatedLabel }}
          </span>
          <LaisPrimaryButton
            v-for="format in exportFormats"
            :key="format.id"
            variant="ghost"
            :icon="
              exportingFormat === format.id
                ? 'i-lucide-loader-circle'
                : format.icon
            "
            :disabled="analyticsTotal === 0 || !!exportingFormat"
            @click="exportarRelatorio(format.id)"
          >
            {{ format.label }}
          </LaisPrimaryButton>
        </div>
        <div
          v-else-if="workspaceTab === 'activity' || workspaceTab === 'duty'"
          class="flex flex-wrap items-center justify-end gap-2"
        >
          <button
            type="button"
            class="grid size-10 place-items-center rounded-full border border-lais-border bg-white text-lais-text-muted hover:border-lais-primary hover:text-lais-primary"
            title="Filtrar"
            aria-label="Filtrar"
            @click="
              useAlert(
                'Use os filtros de período, ordenação e abas para refinar esta visão.'
              )
            "
          >
            <span class="i-lucide-list-filter size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="grid size-10 place-items-center rounded-full border border-lais-border bg-white text-lais-text-muted hover:border-lais-primary hover:text-lais-primary"
            title="Buscar"
            aria-label="Buscar"
            @click="
              useAlert(
                'A busca contextual aparece nas listas e filtros desta área.'
              )
            "
          >
            <span class="i-lucide-search size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="grid size-10 place-items-center rounded-full border border-lais-border bg-white text-lais-text-muted hover:border-lais-primary hover:text-lais-primary"
            title="Informações"
            aria-label="Informações"
            @click="useAlert(workspaceHeaderHelper)"
          >
            <span class="i-lucide-info size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="grid size-10 place-items-center rounded-full border border-lais-border bg-white text-lais-text-muted hover:border-lais-primary hover:text-lais-primary"
            title="Atualizar dados"
            aria-label="Atualizar dados"
            @click="
              workspaceTab === 'duty'
                ? loadOnlineDuty()
                : loadAgentProductivity()
            "
          >
            <span
              class="size-4"
              :class="
                onlineDutyLoading || agentProductivityLoading
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-refresh-cw'
              "
              aria-hidden="true"
            />
          </button>
        </div>
      </template>
    </LaisHeader>

    <div class="border-b border-lais-border-rule px-4 sm:px-6 lg:px-10">
      <div class="flex items-center gap-6 overflow-x-auto no-scrollbar">
        <button
          v-for="item in visibleWorkspaceTabs"
          :key="item.value"
          type="button"
          class="relative inline-flex h-12 shrink-0 items-center gap-2 text-[14px] font-semibold transition-colors"
          :class="
            workspaceTab === item.value
              ? 'text-lais-primary'
              : 'text-lais-text-muted hover:text-lais-text-body'
          "
          :aria-pressed="workspaceTab === item.value"
          @click="workspaceTab = item.value"
        >
          <span class="size-4 shrink-0" :class="item.icon" aria-hidden="true" />
          <span>{{ item.label }}</span>
          <span
            v-if="item.value === 'duty' && onlineDutyPending.length"
            class="rounded-full bg-lais-primary px-2 py-0.5 text-[11px] font-semibold text-white"
          >
            {{ onlineDutyPending.length }}
          </span>
          <span
            v-if="workspaceTab === item.value"
            class="absolute bottom-0 left-0 right-0 h-[3px] rounded-t bg-lais-primary"
          />
        </button>
      </div>
    </div>

    <LaisFilterBar v-if="isReportWorkspace">
      <div class="flex min-w-[160px] flex-1 flex-col gap-1 sm:max-w-[190px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Operação
        </label>
        <select
          v-model="operacao"
          class="h-10 w-full px-3 text-[14px] bg-white border border-lais-border rounded-lais text-lais-text focus:outline-none focus:border-lais-primary"
        >
          <option disabled value="">Escolha uma opção</option>
          <option value="aluguel">Aluguel</option>
          <option value="venda">Venda</option>
        </select>
      </div>
      <div class="flex min-w-[190px] flex-1 flex-col gap-1 sm:max-w-[230px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Período
        </label>
        <select
          v-model="periodo"
          class="h-10 w-full px-3 text-[14px] bg-white border border-lais-border rounded-lais text-lais-text focus:outline-none focus:border-lais-primary"
        >
          <option disabled value="">Escolha uma opção</option>
          <option v-for="p in periodos" :key="p.value" :value="p.value">
            {{ p.label }}
          </option>
        </select>
      </div>
      <div class="min-w-[280px] flex-[2] sm:min-w-[420px]">
        <LaisPropertySearch
          v-model="selectedReportProperty"
          v-model:source="selectedPropertySource"
          v-model:query="reportPropertyQuery"
          include-empreendimentos
          :available-only="false"
          floating-results
          :per-source="6"
          label="Imóvel"
          helper="Busca integrada na carteira interna, catálogo externo e empreendimentos por código, rua, bairro, cidade ou endereço completo."
          placeholder="Código, rua, bairro, cidade ou endereço completo"
          @select="selectReportProperty"
        />
        <p
          v-if="propertyMaterializing"
          class="mt-2 text-[12px] font-semibold text-lais-primary"
        >
          Vinculando imóvel externo para gerar o relatório...
        </p>
      </div>
      <div class="flex min-w-[190px] flex-1 flex-col gap-1 sm:max-w-[230px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Origem
        </label>
        <select
          v-model="analyticsSource"
          class="h-10 w-full px-3 text-[14px] bg-white border border-lais-border rounded-lais text-lais-text focus:outline-none focus:border-lais-primary"
        >
          <option disabled value="">Escolha uma opção</option>
          <option
            v-for="source in analyticsSourceOptions"
            :key="source.value"
            :value="source.value"
          >
            {{ source.label }}
          </option>
        </select>
      </div>
    </LaisFilterBar>

    <ReportOverviewSection
      v-if="isReportWorkspace"
      v-model="reportSection"
      :sections="reportSections"
      :active-section="activeReportSection"
      :section-description="activeReportSectionDescription"
      :scope-badge="reportMetricScopeBadge"
      :cards="reportSectionCards"
    />

    <ReportRankingTabs v-if="isReportWorkspace" v-model="tab" />

    <div v-if="isReportWorkspace" class="p-4 sm:p-6 lg:p-10">
      <ReportLoadingSkeleton v-if="reportLoading" />

      <ReportErrorPanel
        v-else-if="reportError"
        :message="reportError"
        @retry="load"
      />

      <div v-else class="w-full">
        <ReportActivityCards
          :copy="performanceCopy"
          :score="performanceScore"
          :activities="adActivitiesCount"
          :views="viewsCount"
          :scheduled-visits="scheduledVisitsCount"
        />

        <div
          class="mb-6 grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.95fr)]"
        >
          <ReportPropertyAnalysisCard
            :selected-property="selectedProperty"
            :selected-property-label="selectedPropertyLabel"
            :price-analysis="priceAnalysis"
            :owner-share="ownerShare"
            :performance-copy="performanceCopy"
            :is-rental="isRentalReport"
          />

          <div class="grid gap-6 content-start">
            <OwnerShareCard
              :owner-share="ownerShare"
              :price-analysis="priceAnalysis"
              :disabled="!selectedProperty.id"
            />

            <PriceChangeCard
              v-model="priceEditValue"
              :owner="selectedPropertyOwner"
              :is-rental="isRentalReport"
              :validation-message="priceRangeValidationMessage"
              :is-saving="isSavingPrice"
              :disabled="!selectedProperty.id"
              @save="savePriceChange"
            />
          </div>
        </div>

        <div
          class="mb-6 grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(300px,0.9fr)]"
        >
          <ReportPerformanceCard
            :portfolio="portfolio"
            :metric-subject="reportMetricSubject"
            :performance-target="reportMetricPerformanceTarget"
            :proposal-operation-label="proposalOperationLabel"
          />

          <ReportPerformanceInsights
            :section-description="activeReportSectionDescription"
            :portfolio="portfolio"
            :narratives="reportNarratives"
            :metric-subject="reportMetricSubject"
            :metric-verb="reportMetricVerb"
            :proposal-operation-label="proposalOperationLabel"
          />
        </div>

        <div
          class="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(280px,1fr)]"
        >
          <ReportRankingCard
            :title="mainTitle"
            :description="mainDescription"
            :items="topItems"
            :total="total"
          />

          <ReportRankingSidebar
            :tipos="dadosTipos"
            :total-tipos="totalTipos"
            :preco-mediano="precoMediano"
            :operacao="operacao"
            :tab="tab"
            :bairro-destaque="bairroDestaque"
            :imoveis-no-bairro="dadosImoveisNoBairro"
            :total-bairro-destaque="totalBairroDestaque"
          />
        </div>

        <ReportAnalyticsSection
          :funnel="analyticsFunnel"
          :summary-text="analyticsSummaryText"
          :strongest-funnel-value="strongestFunnelValue"
          :top-events="analyticsTopEvents"
          :sources="analyticsSources"
        />

        <ReportAnalyticsTimelineCard
          :timeline="analyticsTimeline"
          :total="analyticsTimelineTotal"
        />
      </div>
    </div>

    <div v-if="workspaceTab === 'activity'" class="p-4 sm:p-6 lg:p-10">
      <div
        class="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.85fr)]"
      >
        <div class="grid gap-6">
          <div
            class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-white p-4 sm:flex-row sm:items-end sm:justify-between"
          >
            <div class="flex min-w-[190px] flex-col gap-1 sm:max-w-[260px]">
              <label
                class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                Período
              </label>
              <select
                v-model="periodo"
                class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
              >
                <option v-for="p in periodos" :key="p.value" :value="p.value">
                  {{ p.label }}
                </option>
              </select>
            </div>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-refresh-cw"
              :is-loading="agentProductivityLoading"
              @click="loadAgentProductivity"
            >
              Atualizar
            </LaisPrimaryButton>
          </div>

          <div
            v-if="agentProductivityError"
            class="rounded-lais border border-lais-danger/30 bg-lais-danger/5 p-4 text-[13px] font-semibold text-lais-danger"
          >
            {{ agentProductivityError }}
          </div>

          <LaisCard>
            <template #header>
              <div class="flex min-w-0 flex-col gap-1">
                <h2
                  class="font-redHatDisplay text-[18px] font-semibold text-lais-text"
                >
                  Meta de visitas finalizadas
                </h2>
                <p class="text-[13px] text-lais-text-muted">
                  {{ activeWorkspaceTab.label }} ·
                  {{ activityPeriodRange.inicio }} a
                  {{ activityPeriodRange.fim }}
                </p>
              </div>
            </template>
            <PerformanceDisplay
              :progress="productivityProgress"
              :actual="productivityActual"
              :target="productivityTargetValue"
              :source="productivityTarget.source || 'default'"
              :agent-name="agentProductivity?.user?.name || 'Corretor logado'"
              :agent-email="agentProductivity?.user?.email"
              :period-label="activityPeriodLabel"
              :loading="agentProductivityLoading && !agentProductivity"
            />
          </LaisCard>

          <ActivityMetricsMenu :groups="productivityGroups" />
        </div>

        <LaisCard title="Indicadores do período">
          <div class="grid gap-3">
            <div
              v-for="group in productivityGroups"
              :key="`summary-${group.key}`"
              class="rounded-lais border border-lais-border-soft bg-white p-3"
            >
              <p class="text-[13px] font-semibold text-lais-text">
                {{ group.title }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                {{ group.metrics.length }} métricas acompanhadas
              </p>
            </div>
          </div>
        </LaisCard>
      </div>
    </div>

    <div v-if="workspaceTab === 'duty'" class="p-4 sm:p-6 lg:p-10">
      <DutyQueueToolbar
        v-model="onlineDutySort"
        :pending-count="onlineDutyPending.length"
        :in-progress-count="onlineDutyInProgress.length"
        :loading="onlineDutyLoading"
        @refresh="loadOnlineDuty"
      />

      <div
        v-if="onlineDutyError"
        class="mb-6 rounded-lais border border-lais-danger/30 bg-lais-danger/5 p-4 text-[13px] font-semibold text-lais-danger"
      >
        {{ onlineDutyError }}
      </div>

      <DutyQueueCarousel
        class="mb-6"
        :conversations="onlineDutyPending"
        :loading="onlineDutyLoading"
        :format-waiting-time="formatWaitingTime"
        @claim="claimOnlineDutyConversation"
        @open="openDutyConversation"
        @copy="copyResolvedSupport"
      />

      <div class="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <DutyPendingList
          :conversations="onlineDutyPending"
          :loading="onlineDutyLoading"
          :has-items="Boolean(hasOnlineDutyItems)"
          :claiming-id="claimingDutyConversationId"
          :captain-suggestions="captainSuggestions"
          :captain-loading-id="captainSuggestionLoadingId"
          @claim="claimOnlineDutyConversation"
          @open="openDutyConversation"
          @copy="copyResolvedSupport"
          @suggest="requestCaptainSuggestion"
          @copy-suggestion="copyCaptainSuggestion"
        />

        <div class="grid gap-6 content-start">
          <DutyInProgressList
            :conversations="onlineDutyInProgress"
            :comment-for="supportCommentFor"
            :saving-id="supportStatusSavingId"
            :captain-suggestions="captainSuggestions"
            :captain-loading-id="captainSuggestionLoadingId"
            @open="openDutyConversation"
            @suggest="requestCaptainSuggestion"
            @copy-suggestion="copyCaptainSuggestion"
            @update-comment="setSupportComment"
            @update-status="updateSupportStatus"
          />

          <SupportTemplateLibrary />
        </div>
      </div>
    </div>

    <!-- SPEC 42 / P37-20 — Relatório mensal operacional -->
    <div v-if="workspaceTab === 'monthly'" class="p-4 sm:p-6 lg:p-10">
      <MonthlyReportPanel />
    </div>

    <div v-if="workspaceTab === 'consent'" class="p-4 sm:p-6 lg:p-10">
      <div class="mx-auto max-w-[820px]">
        <!-- SPEC 29 / P37-4 — Termo de consentimento do embaixador (LGPD) -->
        <ConsentTerm
          :consent="ambassadorConsent"
          :loading="agentConsentsLoading"
          :saving="agentConsentSaving"
          :accept-fn="acceptAmbassadorConsent"
          :revoke-fn="revokeAmbassadorConsent"
        />
      </div>
    </div>

    <DesempenhoEquipe v-if="workspaceTab === 'team'" />
    <MetasIndex v-else-if="workspaceTab === 'targets'" />
  </section>
</template>
